/**
 * useTableSort Hook
 * 
 * Reusable hook for managing table column sorting state.
 * Supports cycling through: Ascending -> Descending -> Reset (null)
 * Only one column can be sorted at a time.
 */

import { useState, useCallback, useMemo } from 'react';
import { SortDescriptor } from '../services/types';

export type SortDirection = 'ascending' | 'descending' | null;

export interface SortState {
  field: string | null;
  direction: SortDirection;
}

export interface UseTableSortOptions {
  /** Default field to sort by */
  defaultField?: string;
  /** Default sort direction */
  defaultDirection?: SortDirection;
}

export interface UseTableSortReturn {
  /** Current sort state */
  sortState: SortState;
  /** Handle sort click - cycles through ascending -> descending -> null */
  handleSort: (field: string) => void;
  /** Reset sort to default or null */
  resetSort: () => void;
  /** Get sorted param for API call */
  sortedParam: SortDescriptor[] | undefined;
  /** Check if a specific field is currently sorted */
  isSorted: (field: string) => boolean;
  /** Get sort direction for a specific field */
  getSortDirection: (field: string) => SortDirection;
}

/**
 * Hook for managing table sorting state
 * 
 * @example
 * const { sortState, handleSort, sortedParam } = useTableSort({
 *   defaultField: 'Id',
 *   defaultDirection: 'descending'
 * });
 * 
 * // In column header
 * <SortableColumnHeader 
 *   title="Status" 
 *   field="Status" 
 *   sortState={sortState} 
 *   onSort={handleSort} 
 * />
 * 
 * // In API call
 * const response = await getControlActivities({
 *   ...params,
 *   sorted: sortedParam
 * });
 */
export const useTableSort = (options: UseTableSortOptions = {}): UseTableSortReturn => {
  const { defaultField = null, defaultDirection = null } = options;

  const [sortState, setSortState] = useState<SortState>({
    field: defaultField,
    direction: defaultDirection,
  });

  /**
   * Handle sort click - cycles through:
   * - If different field: set to ascending
   * - If same field ascending: change to descending
   * - If same field descending: reset to null (or default if provided)
   */
  const handleSort = useCallback((field: string) => {
    setSortState(prev => {
      // If clicking different field, start with ascending
      if (prev.field !== field) {
        return { field, direction: 'ascending' };
      }

      // Same field - cycle through directions
      switch (prev.direction) {
        case 'ascending':
          return { field, direction: 'descending' };
        case 'descending':
          // Reset to default or null
          return { 
            field: defaultField, 
            direction: defaultDirection 
          };
        default:
          return { field, direction: 'ascending' };
      }
    });
  }, [defaultField, defaultDirection]);

  /**
   * Reset sort to default state
   */
  const resetSort = useCallback(() => {
    setSortState({
      field: defaultField,
      direction: defaultDirection,
    });
  }, [defaultField, defaultDirection]);

  /**
   * Convert sort state to API sorted param format
   */
  const sortedParam = useMemo((): SortDescriptor[] | undefined => {
    if (!sortState.field || !sortState.direction) {
      return undefined;
    }
    return [{ name: sortState.field, direction: sortState.direction }];
  }, [sortState]);

  /**
   * Check if a specific field is currently sorted
   */
  const isSorted = useCallback((field: string): boolean => {
    return sortState.field === field && sortState.direction !== null;
  }, [sortState]);

  /**
   * Get sort direction for a specific field
   */
  const getSortDirection = useCallback((field: string): SortDirection => {
    if (sortState.field === field) {
      return sortState.direction;
    }
    return null;
  }, [sortState]);

  return {
    sortState,
    handleSort,
    resetSort,
    sortedParam,
    isSorted,
    getSortDirection,
  };
};

export default useTableSort;
